import { X } from 'lucide-react'
import { type ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { Foot, type FootAction } from './Foot'
import { type RecordAction, RecordActions } from './RecordActions'
import { SectionNav } from './SectionNav'
import { useDismissable } from './useDismissable'
import './foot.css'
import './sheet.css'

export function closesSheetFromBackdrop({
  target,
  currentTarget,
}: {
  target: unknown
  currentTarget: unknown
}): boolean {
  return target === currentTarget
}

/**
 * A panel that opens against the trailing edge of the window, full height.
 *
 * The counterpart to Modal, and which one to use is decided by WHAT is being
 * edited, not by how big the form is. A Sheet holds a RECORD — one row of the
 * list you were just reading. A Modal holds a DECISION you answer and dismiss.
 * Field count is an accident of the record; the kind of thing is not.
 *
 * That is why a sheet does not dim the page: the list beside it is what the
 * record means, so it stays readable. It still covers the page and the page is
 * inert while it is open — a sheet interrupts. A Companion, which accompanies
 * work that continues underneath, is the surface that leaves the page live.
 *
 * `size` widens it for a two-column form, or for a document. `foot` pins actions to
 * the bottom so a long body never scrolls the primary action out of reach; the
 * commit inside `.uifoot__end` spans the column.
 *
 * The foot is two buttons, and a sheet that has one has both. `primary` is the
 * commit, named for what it does — Save changes, Record $50. In a flow of
 * steps it is the final submit, and each step's Next sits under the step.
 * `secondary` leads beside it: Back out of an act the sheet moved to, and
 * Cancel otherwise, which is what it is when a caller names nothing. The primary stays on screen disabled until it can act, so the foot
 * keeps one shape from the moment the sheet opens. Slots rather than buttons
 * each caller places, because an action drawn per call site sits somewhere
 * different on every surface that grows one.
 *
 * Nothing destructive goes in the foot. What can be done TO the record — pay
 * toward it, convert it, void it — is `actions`, one list at the end of the
 * body, with the destructive ones last.
 *
 * `tabs` turns it into the one panel a record's whole configuration lives in.
 * That is the point of having them: a record whose settings are spread over
 * three sheets teaches you three different places to look, and the one you want
 * is always the third. Tabs put every setting behind one opening, with the
 * header stating what the record does so the answer stays on screen while you
 * change the thing that produced it.
 */
export function Sheet<Key extends string>({
  title,
  lead,
  context,
  ariaLabel,
  onClose,
  size = 'default',
  tabs,
  foot,
  primary,
  secondary,
  actions,
  actionsBusy,
  destructive,
  children,
}: {
  title: ReactNode
  /** What this view is for, in a sentence, drawn as the first line of the
   *  body. The header holds the title and nothing else: a sentence there
   *  reads as part of the record's name and pushes it down. */
  lead?: ReactNode
  /**
   * A quiet line of standing context, below the tabs and above the body. It is
   * the resolved answer the panel's fields produce, so it stays true on every
   * tab — which is exactly why it does not belong in the header, where it would
   * push the record's name down and read as part of its identity.
   */
  context?: ReactNode
  ariaLabel?: string
  onClose: () => void
  /**
   * `wide` for a form with two columns of fields; `document` for a body that
   * is a piece of writing with its metadata beside it, which needs a measure
   * rather than a column.
   */
  size?: 'default' | 'wide' | 'doc'
  /** The panel's areas. Omit for a single-purpose sheet — tabs over one tab is
   *  chrome that names what you are already looking at. */
  tabs?: {
    items: readonly { key: Key; label: string }[]
    active: Key
    onChange: (key: Key) => void
  }
  /** A commit drawn by the caller, as `.uifoot__end`. `primary` is the slot a
   *  sheet uses; this remains for a foot whose commit is not one button. */
  foot?: ReactNode
  /** The commit, named for what it does. Disabled until it can act. */
  primary?: FootAction
  /** The action beside the commit — Back or Cancel. Cancel, closing
   *  the sheet, where a sheet with a `primary` names nothing else. */
  secondary?: FootAction
  /**
   * A destructive block drawn after the body and the actions, for a sheet whose
   * delete is a `SettingBlock` rather than an entry in `actions`. A record's
   * acts — destructive ones included — belong in `actions`; this slot holds the
   * block form only. With tabs, pass it only on the tab the record's own
   * settings live on. It is never in the foot.
   */
  destructive?: ReactNode
  /** What can be done to the record, drawn as `RecordActions` after the body. */
  actions?: readonly RecordAction[]
  /** An act is in flight; every action waits for it. */
  actionsBusy?: boolean
  children: ReactNode
}) {
  const panel = useRef<HTMLElement>(null)
  useDismissable(panel, onClose)
  const second =
    secondary ?? (primary ? { label: 'Cancel', onSelect: onClose } : undefined)

  return createPortal(
    <div
      className="sheet-overlay"
      onClick={(e) => closesSheetFromBackdrop(e) && onClose()}
      role="presentation"
    >
      <aside
        className="sheet"
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        ref={panel}
        tabIndex={-1}
      >
        <header className="sheet__head">
          <div className="sheet__heading">
            <h2 className="sheet__title">{title}</h2>
          </div>
          <button
            type="button"
            className="sheet__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        {tabs ? (
          <SectionNav
            variant="sheet"
            label="Settings sections"
            tabs={tabs.items}
            active={tabs.active}
            onChange={tabs.onChange}
          />
        ) : null}
        {/* Standing context sits under the tabs, but is not a band of its own:
            no rule and no fill, so the header stays two stories of structure
            and this reads as a caption on the tab you picked.

            A div, not a p: `context` is commonly a strip or a row of cells, and
            flow content inside a paragraph is invalid nesting React will warn
            about and the browser will silently reparent. */}
        {context ? <div className="sheet__context">{context}</div> : null}
        <div className="sheet__body">
          {/* A div, not a p, for the reason `context` is one: a lead is not
              always a sentence, and flow content inside a paragraph is invalid
              nesting the browser silently reparents. */}
          {lead ? <div className="sheet__lead">{lead}</div> : null}
          {children}
          {actions ? (
            <RecordActions actions={actions} busy={actionsBusy} />
          ) : null}
          {destructive}
        </div>
        {primary ? (
          <Foot primary={primary} secondary={second} className="sheet__foot" />
        ) : foot || second ? (
          <footer
            className={`uifoot sheet__foot${second ? ' uifoot--pair' : ''}`}
          >
            {second ? (
              <Button
                variant="secondary"
                className="uifoot__lead"
                disabled={second.disabled}
                onClick={second.onSelect}
              >
                {second.label}
              </Button>
            ) : null}
            {foot}
          </footer>
        ) : null}
      </aside>
    </div>,
    document.body,
  )
}
